/**
 * About Page - Olympic Fit Wellness
 * Comprehensive about page with what we offer and our approach
 */
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check,
  Dumbbell,
  Apple,
  Heart,
  Brain,
  Users,
  Smartphone,
  Shield,
  Target,
  TrendingUp,
  Activity
} from 'lucide-react';

export default function AboutPage() {
  const differentiators = [
    {
      icon: Target,
      title: 'Comprehensive Wellness Approach',
      description: 'We don\'t just focus on exercise. Our integrated model addresses fitness, nutrition, recovery, mental wellness, and community support.'
    },
    {
      icon: Users,
      title: 'Expert-Led Programs',
      description: 'Every program is designed by certified professionals including NASM trainers, Registered Dietitians, and Certified Health Coaches.'
    },
    {
      icon: Smartphone,
      title: 'Train Anywhere, Anytime',
      description: 'Mobile app with 1,000+ workouts, virtual training, 15+ locations, and 24/7 digital support.'
    },
    {
      icon: Activity,
      title: 'Technology-Enhanced Experience',
      description: 'Smart check-in, automatic workout logging, AI-powered recommendations, and wearable integration.'
    },
    {
      icon: Heart,
      title: 'Community-Centered',
      description: 'Monthly challenges, social events, buddy system, and private member community through our app.'
    },
    {
      icon: TrendingUp,
      title: 'Results-Driven Methodology',
      description: 'Comprehensive assessments, measurable goals, progressive programming, and data-driven adjustments.'
    }
  ];

  const services = [
    {
      category: 'Fitness Training',
      items: ['Personal Training (1-on-1, small group)', 'Group Fitness Classes (500+ weekly)', 'Virtual Workouts', 'Sports-Specific Training', 'Functional Fitness']
    },
    {
      category: 'Nutrition Counseling',
      items: ['Registered Dietitian consultations', 'Custom meal planning', 'Grocery tours & meal prep workshops', 'Metabolic testing', 'Body composition analysis']
    },
    {
      category: 'Recovery & Regeneration',
      items: ['Infrared Saunas', 'Hydromassage', 'Red Light Therapy', 'Cryotherapy', 'Sports Massage']
    },
    {
      category: 'Mental Wellness',
      items: ['Guided Meditation', 'Stress Management workshops', 'Sleep Optimization', 'Mindfulness training', 'Health Coaching']
    }
  ];

  const pillars = [
    { step: '01', title: 'ASSESS', description: 'Comprehensive fitness assessment, health history review, goal clarification, and body composition analysis.' },
    { step: '02', title: 'PLAN', description: 'Customized workout programming, nutrition strategy, recovery protocol, and weekly schedule.' },
    { step: '03', title: 'EXECUTE', description: 'Expert-guided training sessions, access to all services, ongoing coaching, and community support.' },
    { step: '04', title: 'TRACK', description: 'Workout logging, nutrition tracking, body composition changes, and progress photos.' },
    { step: '05', title: 'EVOLVE', description: 'Monthly reviews, program adjustments, new challenges, and long-term sustainability.' }
  ];

  const stats = [
    { value: '15+', label: 'Premium Locations' },
    { value: '50K+', label: 'Active Members' },
    { value: '500+', label: 'Weekly Classes' },
    { value: '100+', label: 'Certified Trainers' },
    { value: '98%', label: 'Satisfaction Rate' },
    { value: '4.9/5', label: 'Average Rating' }
  ];

  return (
    <div className="bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">ABOUT OLYMPIC FIT WELLNESS</span>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            More Than Fitness.<br/>Complete Wellness.
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            A comprehensive wellness clinic where fitness, nutrition, recovery, and community come together to transform lives.
          </p>
        </div>
      </section>

      {/* What is OFW */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">OUR STORY</span>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-6">
                What is Olympic Fit Wellness?
              </h2>
              <div className="space-y-4 text-neutral-600">
                <p>
                  Olympic Fit Wellness is not your typical gym. We're a <strong className="text-neutral-900">comprehensive wellness clinic</strong> that addresses every aspect of your health journey—physical fitness, nutrition, mental wellness, recovery, and community support.
                </p>
                <p>
                  Founded in 2015 in McKinney, Texas, we've grown to 15+ locations serving over 50,000 members because we offer something different: a holistic approach to health that goes far beyond treadmills and dumbbells.
                </p>
                <p>
                  Whether you're training for a marathon, recovering from an injury, managing stress, or simply want to feel better in your daily life, Olympic Fit Wellness provides the expert guidance, state-of-the-art facilities, and supportive community you need to succeed.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-neutral-50 rounded-lg">
                <p className="font-heading font-bold text-neutral-900 mb-2">Our Philosophy</p>
                <p className="text-neutral-600 italic">"Train Anywhere, Anytime"</p>
                <p className="text-sm text-neutral-500 mt-2">
                  We believe wellness shouldn't be confined to a building or restricted by your schedule. That's why we've built a flexible ecosystem that meets you where you are.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800"
                alt="Olympic Fit Wellness Facility"
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#3B82F6] text-white p-6 rounded-lg">
                <div className="text-3xl font-heading font-bold">2015</div>
                <div className="text-sm">Founded in McKinney, TX</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">WHY OFW</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
              What Makes Olympic Fit Wellness Different?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map((item, idx) => (
              <div key={idx} className="bg-white p-8 rounded-lg shadow-sm">
                <div className="w-12 h-12 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-heading font-bold text-neutral-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-neutral-600 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">WHAT WE OFFER</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
              Comprehensive Wellness Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="border border-neutral-200 rounded-lg p-6">
                <h3 className="text-lg font-heading font-bold text-neutral-900 mb-4 pb-4 border-b border-neutral-100">
                  {service.category}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
                      <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors"
            >
              EXPLORE ALL SERVICES <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* The OFW Method */}
      <section className="py-20 lg:py-28 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">OUR APPROACH</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              The Olympic Fit Wellness Method
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              Our 5-pillar framework combines evidence-based practices with real-world application.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-heading font-bold text-[#3B82F6]/30 mb-4">
                  {pillar.step}
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-3">
                  {pillar.title}
                </h3>
                <p className="text-neutral-400 text-sm">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">BY THE NUMBERS</span>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
              Olympic Fit Wellness Impact
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-heading font-bold text-[#3B82F6] mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-neutral-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">OUR COMMITMENT</span>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-neutral-900 mb-4">
                The Olympic Fit Wellness Promise
              </h2>
            </div>

            <div className="space-y-4">
              {[
                { title: 'Expert Guidance', desc: 'Every trainer, coach, and instructor is certified and continuously educated.' },
                { title: 'Personalized Attention', desc: 'No cookie-cutter programs. Every plan is tailored to your unique goals.' },
                { title: 'Safe Environment', desc: 'Clean, well-maintained facilities with strict safety protocols.' },
                { title: 'Inclusive Community', desc: 'All fitness levels, ages, and backgrounds welcome in our judgment-free space.' },
                { title: 'Transparent Pricing', desc: 'No hidden fees or surprise charges. What you see is what you pay.' },
                { title: 'Results or Support', desc: 'If you\'re not seeing progress, we\'ll work with you until you do.' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 bg-white p-6 rounded-lg">
                  <Check className="w-6 h-6 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading font-bold text-neutral-900">{item.title}</h4>
                    <p className="text-neutral-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
            Experience Olympic Fit Wellness
          </h2>
          <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
            Ready to see what makes us different? Schedule a tour, try a free class, or start your 7-day free trial.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/locations" className="bg-white text-black font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-100 transition-colors inline-flex items-center gap-2">
              FIND A LOCATION <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/membership" className="border border-white text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-white/10 transition-colors">
              START FREE TRIAL
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
